
// /**
//  * UserManagementPage.tsx — manage tenant users via Keycloak Admin API.
//  *
//  * Bugs fixed in this revision:
//  *
//  * BUG-1 (PAGE REFRESH — primary): The invite dialog used <Dialog> without
//  *   <DialogContent>. The <form onSubmit> was placed naked inside <Dialog>
//  *   with a rogue <DialogClose onClose={...}> prop (invalid on Radix) and no
//  *   portal boundary. Result: form submission triggered a real browser
//  *   navigation / page reload instead of calling handleInvite. Fixed by
//  *   wrapping all dialog children inside proper <DialogContent>.
//  *
//  * BUG-2 (GET response shape mismatch): Backend GET /api/v1/users returns
//  *   UserResponse[] with fields {id, email, firstName, lastName, role, enabled,
//  *   tenant_slug, displayName}. Frontend UserRow expected {name, status,
//  *   lastLogin} which don't exist, so the query always fell back to MOCK_USERS.
//  *   Fixed: UserRow now matches UserResponse exactly; name/status are derived
//  *   at render time from firstName+lastName and enabled fields.
//  *
//  * BUG-3 (invite never sent): handleInvite never set sendInvitation:true in
//  *   the POST body. Backend defaulted to false → Keycloak created the user
//  *   silently without a VERIFY_EMAIL required action and without sending an
//  *   invite email. Fixed: sendInvitation is now always true for invites; a
//  *   "Set password now" toggle allows setting a temporaryPassword instead.
//  *
//  * BUG-4 (role update body shape): PUT /api/v1/users/:id was sent with
//  *   {status: "deactivated"} but UserUpdate has no status field — it uses
//  *   {enabled: boolean}. Fixed toggleStatus to send {enabled: boolean}.
//  */
// import { useState } from "react";
// import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
// import {
//   CheckCircle2,
//   Loader2,
//   Mail,
//   MoreVertical,
//   ShieldCheck,
//   UserMinus,
//   UserPlus,
// } from "lucide-react";
// import { toast } from "sonner";

// import { http } from "@/services/apiClient";
// import { Pagination, usePagination } from "@/components/ui/pagination";
// import type { Role } from "@/types/common";
// import { PageHeader } from "@/components/ui/page-header";
// import { Button } from "@/components/ui/button";
// import {
//   Card,
//   CardContent,
//   CardDescription,
//   CardHeader,
//   CardTitle,
// } from "@/components/ui/card";
// import {
//   Table,
//   TableBody,
//   TableCell,
//   TableHead,
//   TableHeader,
//   TableRow,
// } from "@/components/ui/table";
// import { Badge } from "@/components/ui/badge";
// import { Input } from "@/components/ui/input";
// import { Label } from "@/components/ui/label";
// import { NativeSelect } from "@/components/ui/select";
// import { Skeleton } from "@/components/ui/skeleton";
// import {
//   Dialog,
//   DialogContent,
//   DialogDescription,
//   DialogFooter,
//   DialogHeader,
//   DialogTitle,
// } from "@/components/ui/dialog";
// import {
//   DropdownMenu,
//   DropdownMenuContent,
//   DropdownMenuItem,
//   DropdownMenuLabel,
//   DropdownMenuSeparator,
//   DropdownMenuTrigger,
// } from "@/components/ui/dropdown-menu";
// import {
//   Tooltip,
//   TooltipContent,
//   TooltipTrigger,
// } from "@/components/ui/tooltip";
// import { EmptyState } from "@/components/ui/empty-state";
// import { initials } from "@/lib/utils";

// // ─── Types — matches backend UserResponse exactly ────────────────────────────

// interface UserResponse {
//   id: string;
//   email: string | null;
//   firstName: string | null;
//   lastName: string | null;
//   role: Role | null;
//   enabled: boolean;
//   tenant_slug: string | null;
//   displayName: string | null;
// }

// // ─── Helpers ─────────────────────────────────────────────────────────────────

// function displayName(u: UserResponse): string {
//   if (u.displayName) return u.displayName;
//   const full = [u.firstName, u.lastName].filter(Boolean).join(" ");
//   return full || u.email || "—";
// }

// function userStatus(u: UserResponse): "Active" | "Deactivated" {
//   return u.enabled ? "Active" : "Deactivated";
// }

// const ROLE_OPTIONS: Role[] = ["REP", "MANAGER", "TENANT_ADMIN"];

// function roleVariant(role: Role | null): "default" | "secondary" | "destructive" | "outline" {
//   switch (role) {
//     case "SUPER_ADMIN":    return "destructive";
//     case "TENANT_ADMIN":   return "default";
//     case "MANAGER":        return "secondary";
//     case "REP":            return "secondary";
//     default:               return "outline";
//   }
// }

// function statusVariant(enabled: boolean): "default" | "secondary" {
//   return enabled ? "default" : "secondary";
// }

// // ─── Component ───────────────────────────────────────────────────────────────

// export function UserManagementPage() {
//   const queryClient = useQueryClient();

//   // ── Invite dialog state ───────────────────────────────────────────────────
//   const [inviteOpen, setInviteOpen]           = useState(false);
//   const [inviteEmail, setInviteEmail]         = useState("");
//   const [inviteFirstName, setInviteFirstName] = useState("");
//   const [inviteLastName, setInviteLastName]   = useState("");
//   const [inviteRole, setInviteRole]           = useState<Role>("REP");
//   // "Set password now" toggle — when true, send a temporaryPassword instead
//   // of an invite email (useful for internal/SSO-less tenants).
//   const [setPasswordNow, setSetPasswordNow]   = useState(true);
//   const [tempPassword, setTempPassword]       = useState("");

//   // ── Server state ──────────────────────────────────────────────────────────
//   // BUG-2 FIX: useQuery now expects list[UserResponse] (the actual backend shape).
//   const { data, isLoading } = useQuery({
//     queryKey: ["users"],
//     queryFn: () => http.get<UserResponse[]>("/api/v1/users"),
//   });

//   const users: UserResponse[] = data ?? [];

//   const { page, pageSize, total, pageItems, setPage, setPageSize } =
//     usePagination({ items: users, initialPageSize: 15 });

//   // ── Mutations ─────────────────────────────────────────────────────────────

//   const inviteMutation = useMutation({
//     mutationFn: (body: {
//       email: string;
//       firstName: string;
//       lastName: string;
//       role: Role;
//       sendInvitation: boolean;
//       temporaryPassword?: string;
//     }) => http.post<UserResponse>("/api/v1/users", body),
//     onSuccess: (_data, vars) => {
//       toast.success(
//         vars.sendInvitation
//           ? `Invite sent to ${vars.email}`
//           : `User ${vars.email} created`,
//       );
//       queryClient.invalidateQueries({ queryKey: ["users"] });
//       closeInviteDialog();
//     },
//     onError: (err: unknown) => {
//       const msg = err instanceof Error ? err.message : "Failed to create user";
//       toast.error(msg);
//     },
//   });

//   const updateMutation = useMutation({
//     mutationFn: ({ id, body }: { id: string; body: { role?: Role; enabled?: boolean } }) =>
//       http.put<UserResponse>(`/api/v1/users/${id}`, body),
//     onSuccess: () => {
//       toast.success("User updated");
//       queryClient.invalidateQueries({ queryKey: ["users"] });
//     },
//     onError: () => toast.error("Failed to update user"),
//   });

//   const deleteMutation = useMutation({
//     mutationFn: (id: string) => http.delete(`/api/v1/users/${id}`),
//     onSuccess: () => {
//       toast.success("User deleted");
//       queryClient.invalidateQueries({ queryKey: ["users"] });
//     },
//     onError: () => toast.error("Failed to delete user"),
//   });

//   // ── Handlers ─────────────────────────────────────────────────────────────

//   function closeInviteDialog() {
//     setInviteOpen(false);
//     setInviteEmail("");
//     setInviteFirstName("");
//     setInviteLastName("");
//     setInviteRole("REP");
//     setSetPasswordNow(false);
//     setTempPassword("");
//   }

//   // BUG-1 FIX: handleInvite is called from a Button onClick (not form onSubmit)
//   // so there is no HTML form submission to cause a page reload.
//   // BUG-3 FIX: sendInvitation is true by default; temporaryPassword only sent
//   // when the "set password now" toggle is active.
//   function handleInvite() {
//     const email = inviteEmail.trim();
//     if (!email || !email.includes("@")) {
//       toast.error("Enter a valid email address");
//       return;
//     }
//     if (!inviteFirstName.trim()) {
//       toast.error("First name is required");
//       return;
//     }
//     if (!inviteLastName.trim()) {
//       toast.error("Last name is required");
//       return;
//     }
//     if (setPasswordNow && tempPassword.length < 8) {
//       toast.error("Password must be at least 8 characters");
//       return;
//     }

//     inviteMutation.mutate({
//       email,
//       firstName: inviteFirstName.trim(),
//       lastName:  inviteLastName.trim(),
//       role:      inviteRole,
//       // BUG-3 FIX: always send sendInvitation unless using password mode
//       sendInvitation: !setPasswordNow,
//       temporaryPassword: setPasswordNow ? tempPassword : undefined,
//     });
//   }

//   function changeRole(u: UserResponse, role: Role) {
//     updateMutation.mutate({ id: u.id, body: { role } });
//   }

//   // BUG-4 FIX: send {enabled: boolean}, not {status: string} — UserUpdate
//   // has no status field; backend uses enabled:bool to activate/deactivate.
//   function toggleEnabled(u: UserResponse) {
//     updateMutation.mutate({ id: u.id, body: { enabled: !u.enabled } });
//   }

//   // ── Render ────────────────────────────────────────────────────────────────

//   return (
//     <div className="space-y-6">
//       <PageHeader
//         title="User Management"
//         description="Invite team members, assign roles, and manage account status. Backed by Keycloak Admin API."
//         actions={
//           <Button onClick={() => setInviteOpen(true)}>
//             <UserPlus className="h-4 w-4" />
//             Add User
//           </Button>
//         }
//       />

//       <Card>
//         <CardHeader>
//           <CardTitle>Team Members</CardTitle>
//           <CardDescription>
//             {users.length} user{users.length === 1 ? "" : "s"} in this tenant.
//           </CardDescription>
//         </CardHeader>
//         <CardContent>
//           {isLoading ? (
//             <div className="space-y-2">
//               {Array.from({ length: 5 }).map((_, i) => (
//                 <Skeleton key={i} className="h-12 w-full" />
//               ))}
//             </div>
//           ) : users.length === 0 ? (
//             <EmptyState
//               icon={<UserPlus className="h-6 w-6" />}
//               title="No users yet"
//               description="Invite your first team member to get started."
//               action={
//                 <Button onClick={() => setInviteOpen(true)}>
//                   <UserPlus className="h-4 w-4" />
//                   Add User
//                 </Button>
//               }
//             />
//           ) : (
//             <Table>
//               <TableHeader>
//                 <TableRow>
//                   <TableHead>Name</TableHead>
//                   <TableHead>Email</TableHead>
//                   <TableHead>Role</TableHead>
//                   <TableHead>Status</TableHead>
//                   <TableHead>Actions</TableHead>
//                 </TableRow>
//               </TableHeader>
//               <TableBody>
//                 {pageItems.map((u) => (
//                   <TableRow key={u.id}>
//                     <TableCell>
//                       <div className="flex items-center gap-3">
//                         <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-xs font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
//                           {initials(displayName(u))}
//                         </div>
//                         <span className="font-medium">{displayName(u)}</span>
//                       </div>
//                     </TableCell>
//                     <TableCell className="text-muted-foreground">
//                       {u.email ?? "—"}
//                     </TableCell>
//                     <TableCell>
//                       <Badge variant={roleVariant(u.role)}>
//                         {u.role ?? "—"}
//                       </Badge>
//                     </TableCell>
//                     <TableCell>
//                       <Badge variant={statusVariant(u.enabled)}>
//                         {userStatus(u)}
//                       </Badge>
//                     </TableCell>
//                     <TableCell>
//                       <DropdownMenu>
//                         <Tooltip>
//                           <TooltipTrigger asChild>
//                             <DropdownMenuTrigger asChild>
//                               <Button variant="ghost" size="icon" aria-label="User actions">
//                                 <MoreVertical className="h-4 w-4" />
//                               </Button>
//                             </DropdownMenuTrigger>
//                           </TooltipTrigger>
//                           <TooltipContent>User actions</TooltipContent>
//                         </Tooltip>
//                         <DropdownMenuContent align="end">
//                           <DropdownMenuLabel>Change role</DropdownMenuLabel>
//                           {ROLE_OPTIONS.map((r) => (
//                             <DropdownMenuItem
//                               key={r}
//                               onClick={() => changeRole(u, r)}
//                             >
//                               <span className="flex items-center justify-between gap-2 w-full">
//                                 {r}
//                                 {u.role === r && (
//                                   <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
//                                 )}
//                               </span>
//                             </DropdownMenuItem>
//                           ))}
//                           <DropdownMenuSeparator />
//                           <DropdownMenuItem onClick={() => toggleEnabled(u)}>
//                             <UserMinus className="h-3.5 w-3.5" />
//                             {u.enabled ? "Deactivate user" : "Reactivate user"}
//                           </DropdownMenuItem>
//                           <DropdownMenuItem
//                             destructive
//                             onClick={() => deleteMutation.mutate(u.id)}
//                           >
//                             Permanently delete
//                           </DropdownMenuItem>
//                         </DropdownMenuContent>
//                       </DropdownMenu>
//                     </TableCell>
//                   </TableRow>
//                 ))}
//               </TableBody>
//             </Table>
//           )}

//           <Pagination
//             page={page}
//             pageSize={pageSize}
//             total={total}
//             onPageChange={setPage}
//             onPageSizeChange={setPageSize}
//           />
//         </CardContent>
//       </Card>

//       {/*
//         BUG-1 FIX: All dialog children are now inside <DialogContent> so
//         Radix mounts a proper portal overlay and no HTML <form> is rendered
//         in the main page DOM.  The submit button uses onClick={handleInvite}
//         (not type="submit") so there is no form submission event to intercept.
//       */}
//       <Dialog open={inviteOpen} onOpenChange={(o) => { if (!o) closeInviteDialog(); else setInviteOpen(true); }}>
//         <DialogContent className="max-w-md">
//           <DialogHeader>
//             <DialogTitle>Add User</DialogTitle>
//             <DialogDescription>
//               {setPasswordNow
//                 ? "Create the user with a password."
//                 : "An User will be created with the mentioned deatils and password."}
//             </DialogDescription>
//           </DialogHeader>

//           <div className="space-y-4 py-1">
//             {/* Email */}
//             <div className="space-y-2">
//               <Label htmlFor="inv-email">Email address *</Label>
//               <div className="relative">
//                 <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
//                 <Input
//                   id="inv-email"
//                   type="email"
//                   placeholder="teammate@yourcompany.com"
//                   value={inviteEmail}
//                   onChange={(e) => setInviteEmail(e.target.value)}
//                   className="pl-9"
//                 />
//               </div>
//             </div>

//             {/* Name */}
//             <div className="grid grid-cols-2 gap-3">
//               <div className="space-y-2">
//                 <Label htmlFor="inv-first">First name *</Label>
//                 <Input
//                   id="inv-first"
//                   placeholder="Jane"
//                   value={inviteFirstName}
//                   onChange={(e) => setInviteFirstName(e.target.value)}
//                 />
//               </div>
//               <div className="space-y-2">
//                 <Label htmlFor="inv-last">Last name *</Label>
//                 <Input
//                   id="inv-last"
//                   placeholder="Smith"
//                   value={inviteLastName}
//                   onChange={(e) => setInviteLastName(e.target.value)}
//                 />
//               </div>
//             </div>

//             {/* Role */}
//             <div className="space-y-2">
//               <Label htmlFor="inv-role">Role</Label>
//               <NativeSelect
//                 id="inv-role"
//                 value={inviteRole}
//                 onChange={(e) => setInviteRole(e.target.value as Role)}
//               >
//                 {ROLE_OPTIONS.map((r) => (
//                   <option key={r} value={r}>{r}</option>
//                 ))}
//               </NativeSelect>
//               <p className="text-xs text-muted-foreground">
//                 REP — run outreach · MANAGER — view all reps · TENANT_ADMIN — full tenant control.
//               </p>
//             </div>

//             {/* Password toggle */}
//             <div className="flex items-center gap-3 rounded-lg border p-3 bg-muted/30">
//               <ShieldCheck className="h-4 w-4 text-muted-foreground shrink-0" />
//               <div className="flex-1 text-sm">
//                 <p className="font-medium">Set password now</p>
//                 {/* <p className="text-xs text-muted-foreground">
//                   Skip invite email; set a temporary password instead.
//                 </p> */}
//               </div>
//               <button
//                 type="button"
//                 className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
//                   setPasswordNow ? "bg-primary" : "bg-muted-foreground/30"
//                 }`}
//                 onClick={() => { setSetPasswordNow((v) => !v); setTempPassword(""); }}
//                 role="switch"
//                 aria-checked={setPasswordNow}
//               >
//                 <span
//                   className={`inline-block h-3 w-3 rounded-full bg-white transition-transform mx-1 ${
//                     setPasswordNow ? "translate-x-4" : "translate-x-0"
//                   }`}
//                 />
//               </button>
//             </div>

//             {setPasswordNow && (
//               <div className="space-y-2">
//                 <Label htmlFor="inv-password">Temporary password (min 8 chars)</Label>
//                 <Input
//                   id="inv-password"
//                   type="password"
//                   placeholder="••••••••"
//                   value={tempPassword}
//                   onChange={(e) => setTempPassword(e.target.value)}
//                   minLength={8}
//                 />
//               </div>
//             )}
//           </div>

//           <DialogFooter>
//             <Button variant="outline" onClick={closeInviteDialog}>
//               Cancel
//             </Button>
//             <Button
//               onClick={handleInvite}
//               disabled={inviteMutation.isPending}
//             >
//               {inviteMutation.isPending
//                 ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…</>
//                 : setPasswordNow ? "Create user" : "Send invite"
//               }
//             </Button>
//           </DialogFooter>
//         </DialogContent>
//       </Dialog>
//     </div>
//   );
// }

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AtSign,
  CheckCircle2,
  Loader2,
  Mail,
  MoreVertical,
  Pencil,
  ShieldCheck,
  UserMinus,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";

import { http } from "@/services/apiClient";
import { Pagination, usePagination } from "@/components/ui/pagination";
import type { Role } from "@/types/common";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { EmptyState } from "@/components/ui/empty-state";
import { UsernameCheckHint } from "@/components/UsernameCheckHint";
import { initials } from "@/lib/utils";
import {
  apiErrorDetail,
  effectiveUsername,
  normalizeUsername,
  usernameFormatError,
  useUsernameAvailability,
} from "@/lib/username";

const USERNAME_CHECK_ENDPOINT = "/api/v1/users/username-available";

// ─── Types — matches backend UserResponse exactly ────────────────────────────

interface UserResponse {
  id: string;
  email: string | null;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  role: Role | null;
  enabled: boolean;
  tenant_slug: string | null;
  displayName: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function displayName(u: UserResponse): string {
  if (u.displayName) return u.displayName;
  const full = [u.firstName, u.lastName].filter(Boolean).join(" ");
  return full || u.email || "—";
}

function userStatus(u: UserResponse): "Active" | "Deactivated" {
  return u.enabled ? "Active" : "Deactivated";
}

const ROLE_OPTIONS: Role[] = ["REP", "MANAGER", "TENANT_ADMIN"];

function roleVariant(role: Role | null): "default" | "secondary" | "destructive" | "outline" {
  switch (role) {
    case "SUPER_ADMIN":    return "destructive";
    case "TENANT_ADMIN":   return "default";
    case "MANAGER":        return "secondary";
    case "REP":            return "secondary";
    default:               return "outline";
  }
}

function statusVariant(enabled: boolean): "default" | "secondary" {
  return enabled ? "default" : "secondary";
}

// ─── Component ───────────────────────────────────────────────────────────────

export function UserManagementPage() {
  const queryClient = useQueryClient();

  // ── Invite dialog state ───────────────────────────────────────────────────
  const [inviteOpen, setInviteOpen]           = useState(false);
  const [inviteEmail, setInviteEmail]         = useState("");
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName]   = useState("");
  const [inviteRole, setInviteRole]           = useState<Role>("REP");
  // "Set password now" toggle — when true, send a temporaryPassword instead
  // of an invite email (useful for internal/SSO-less tenants).
  const [setPasswordNow, setSetPasswordNow]   = useState(true);
  const [tempPassword, setTempPassword]       = useState("");
  // Optional login username — blank means "use the email as the username".
  const [inviteUsername, setInviteUsername]   = useState("");

  // ── Change-username dialog state ──────────────────────────────────────────
  const [renameUser, setRenameUser]           = useState<UserResponse | null>(null);
  const [renameValue, setRenameValue]         = useState("");

  // Live availability checks (debounced, server-side uniqueness).
  const inviteCandidate = effectiveUsername(inviteUsername, inviteEmail.includes("@") ? inviteEmail : "");
  const inviteUsesEmail = !normalizeUsername(inviteUsername);
  const inviteCheck = useUsernameAvailability(
    inviteCandidate,
    USERNAME_CHECK_ENDPOINT,
    undefined,
    inviteOpen,
  );
  const renameCheck = useUsernameAvailability(
    renameValue,
    USERNAME_CHECK_ENDPOINT,
    renameUser?.id,
    renameUser !== null,
  );

  // ── Server state ──────────────────────────────────────────────────────────
  // BUG-2 FIX: useQuery now expects list[UserResponse] (the actual backend shape).
  const { data, isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => http.get<UserResponse[]>("/api/v1/users"),
  });

  const users: UserResponse[] = data ?? [];

  const { page, pageSize, total, pageItems, setPage, setPageSize } =
    usePagination({ items: users, initialPageSize: 15 });

  // ── Mutations ─────────────────────────────────────────────────────────────

  const inviteMutation = useMutation({
    mutationFn: (body: {
      email: string;
      username?: string;
      firstName: string;
      lastName: string;
      role: Role;
      sendInvitation: boolean;
      temporaryPassword?: string;
    }) => http.post<UserResponse>("/api/v1/users", body),
    onSuccess: (data, vars) => {
      const login = data?.username ?? vars.username ?? vars.email;
      toast.success(
        vars.sendInvitation
          ? `Invite sent to ${vars.email} — username: ${login}`
          : `User created — username: ${login}`,
      );
      queryClient.invalidateQueries({ queryKey: ["users"] });
      closeInviteDialog();
    },
    onError: (err: unknown) => {
      toast.error(apiErrorDetail(err, "Failed to create user"));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { role?: Role; enabled?: boolean; username?: string };
    }) => http.put<UserResponse>(`/api/v1/users/${id}`, body),
    onSuccess: (_data, vars) => {
      toast.success(
        vars.body.username ? `Username changed to ${vars.body.username}` : "User updated",
      );
      queryClient.invalidateQueries({ queryKey: ["users"] });
      if (vars.body.username) closeRenameDialog();
    },
    onError: (err: unknown) => toast.error(apiErrorDetail(err, "Failed to update user")),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => http.delete(`/api/v1/users/${id}`),
    onSuccess: () => {
      toast.success("User deleted");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: () => toast.error("Failed to delete user"),
  });

  // ── Handlers ─────────────────────────────────────────────────────────────

  function closeInviteDialog() {
    setInviteOpen(false);
    setInviteEmail("");
    setInviteFirstName("");
    setInviteLastName("");
    setInviteRole("REP");
    setSetPasswordNow(false);
    setTempPassword("");
    setInviteUsername("");
  }

  function openRenameDialog(u: UserResponse) {
    setRenameUser(u);
    setRenameValue(u.username ?? "");
  }

  function closeRenameDialog() {
    setRenameUser(null);
    setRenameValue("");
  }

  function handleRename() {
    if (!renameUser) return;
    const next = normalizeUsername(renameValue);
    const formatError = usernameFormatError(next);
    if (formatError) {
      toast.error(formatError);
      return;
    }
    if (next === normalizeUsername(renameUser.username)) {
      closeRenameDialog();
      return;
    }
    if (renameCheck.status === "taken") {
      toast.error(renameCheck.message);
      return;
    }
    updateMutation.mutate({ id: renameUser.id, body: { username: next } });
  }

  // BUG-1 FIX: handleInvite is called from a Button onClick (not form onSubmit)
  // so there is no HTML form submission to cause a page reload.
  // BUG-3 FIX: sendInvitation is true by default; temporaryPassword only sent
  // when the "set password now" toggle is active.
  function handleInvite() {
    const email = inviteEmail.trim();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    if (!inviteFirstName.trim()) {
      toast.error("First name is required");
      return;
    }
    if (!inviteLastName.trim()) {
      toast.error("Last name is required");
      return;
    }
    if (setPasswordNow && tempPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    const username = normalizeUsername(inviteUsername);
    if (username) {
      const formatError = usernameFormatError(username);
      if (formatError) {
        toast.error(formatError);
        return;
      }
    }
    if (inviteCheck.status === "taken") {
      toast.error(
        inviteUsesEmail
          ? `${inviteCheck.message} Enter a username to reuse this email.`
          : inviteCheck.message,
      );
      return;
    }

    inviteMutation.mutate({
      email,
      // Blank → omitted → backend uses the email as the username.
      ...(username ? { username } : {}),
      firstName: inviteFirstName.trim(),
      lastName:  inviteLastName.trim(),
      role:      inviteRole,
      // BUG-3 FIX: always send sendInvitation unless using password mode
      sendInvitation: !setPasswordNow,
      temporaryPassword: setPasswordNow ? tempPassword : undefined,
    });
  }

  function changeRole(u: UserResponse, role: Role) {
    updateMutation.mutate({ id: u.id, body: { role } });
  }

  // BUG-4 FIX: send {enabled: boolean}, not {status: string} — UserUpdate
  // has no status field; backend uses enabled:bool to activate/deactivate.
  function toggleEnabled(u: UserResponse) {
    updateMutation.mutate({ id: u.id, body: { enabled: !u.enabled } });
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <PageHeader
        title="User Management"
        description="Invite team members, assign roles, and manage account status. Backed by Keycloak Admin API."
        actions={
          <Button onClick={() => setInviteOpen(true)}>
            <UserPlus className="h-4 w-4" />
            Add User
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Team Members</CardTitle>
          <CardDescription>
            {users.length} user{users.length === 1 ? "" : "s"} in this tenant.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : users.length === 0 ? (
            <EmptyState
              icon={<UserPlus className="h-6 w-6" />}
              title="No users yet"
              description="Invite your first team member to get started."
              action={
                <Button onClick={() => setInviteOpen(true)}>
                  <UserPlus className="h-4 w-4" />
                  Add User
                </Button>
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageItems.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-xs font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                          {initials(displayName(u))}
                        </div>
                        <span className="font-medium">{displayName(u)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {u.username ?? "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {u.email ?? "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={roleVariant(u.role)}>
                        {u.role ?? "—"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(u.enabled)}>
                        {userStatus(u)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" aria-label="User actions">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                          </TooltipTrigger>
                          <TooltipContent>User actions</TooltipContent>
                        </Tooltip>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Change role</DropdownMenuLabel>
                          {ROLE_OPTIONS.map((r) => (
                            <DropdownMenuItem
                              key={r}
                              onClick={() => changeRole(u, r)}
                            >
                              <span className="flex items-center justify-between gap-2 w-full">
                                {r}
                                {u.role === r && (
                                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                                )}
                              </span>
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => openRenameDialog(u)}>
                            <Pencil className="h-3.5 w-3.5" />
                            Change username
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleEnabled(u)}>
                            <UserMinus className="h-3.5 w-3.5" />
                            {u.enabled ? "Deactivate user" : "Reactivate user"}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            destructive
                            onClick={() => deleteMutation.mutate(u.id)}
                          >
                            Permanently delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </CardContent>
      </Card>

      {/*
        BUG-1 FIX: All dialog children are now inside <DialogContent> so
        Radix mounts a proper portal overlay and no HTML <form> is rendered
        in the main page DOM.  The submit button uses onClick={handleInvite}
        (not type="submit") so there is no form submission event to intercept.
      */}
      <Dialog open={inviteOpen} onOpenChange={(o) => { if (!o) closeInviteDialog(); else setInviteOpen(true); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
            <DialogDescription>
              {setPasswordNow
                ? "Create the user with a password."
                : "An User will be created with the mentioned deatils and password."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="inv-email">Email address *</Label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="inv-email"
                  type="email"
                  placeholder="teammate@yourcompany.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Username (optional — Salesforce-style login identifier) */}
            <div className="space-y-2">
              <Label htmlFor="inv-username">Username</Label>
              <div className="relative">
                <AtSign className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="inv-username"
                  placeholder="Leave blank to use the email"
                  value={inviteUsername}
                  onChange={(e) => setInviteUsername(e.target.value)}
                  className="pl-9"
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
              <UsernameCheckHint
                state={inviteCheck}
                usingEmailDefault={inviteUsesEmail}
                idleHint="Used to sign in. Must be unique across Outrena — the same email can have several usernames."
              />
            </div>

            {/* Name */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="inv-first">First name *</Label>
                <Input
                  id="inv-first"
                  placeholder="Jane"
                  value={inviteFirstName}
                  onChange={(e) => setInviteFirstName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inv-last">Last name *</Label>
                <Input
                  id="inv-last"
                  placeholder="Smith"
                  value={inviteLastName}
                  onChange={(e) => setInviteLastName(e.target.value)}
                />
              </div>
            </div>

            {/* Role */}
            <div className="space-y-2">
              <Label htmlFor="inv-role">Role</Label>
              <NativeSelect
                id="inv-role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as Role)}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </NativeSelect>
              <p className="text-xs text-muted-foreground">
                REP — run outreach · MANAGER — view all reps · TENANT_ADMIN — full tenant control.
              </p>
            </div>

            {/* Password toggle */}
            <div className="flex items-center gap-3 rounded-lg border p-3 bg-muted/30">
              <ShieldCheck className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1 text-sm">
                <p className="font-medium">Set password now</p>
                {/* <p className="text-xs text-muted-foreground">
                  Skip invite email; set a temporary password instead.
                </p> */}
              </div>
              <button
                type="button"
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  setPasswordNow ? "bg-primary" : "bg-muted-foreground/30"
                }`}
                onClick={() => { setSetPasswordNow((v) => !v); setTempPassword(""); }}
                role="switch"
                aria-checked={setPasswordNow}
              >
                <span
                  className={`inline-block h-3 w-3 rounded-full bg-white transition-transform mx-1 ${
                    setPasswordNow ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            {setPasswordNow && (
              <div className="space-y-2">
                <Label htmlFor="inv-password">Temporary password (min 8 chars)</Label>
                <Input
                  id="inv-password"
                  type="password"
                  placeholder="••••••••"
                  value={tempPassword}
                  onChange={(e) => setTempPassword(e.target.value)}
                  minLength={8}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeInviteDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleInvite}
              disabled={
                inviteMutation.isPending ||
                inviteCheck.status === "taken" ||
                inviteCheck.status === "invalid"
              }
            >
              {inviteMutation.isPending
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…</>
                : setPasswordNow ? "Create user" : "Send invite"
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change-username dialog */}
      <Dialog open={renameUser !== null} onOpenChange={(o) => { if (!o) closeRenameDialog(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change username</DialogTitle>
            <DialogDescription>
              {renameUser
                ? `${displayName(renameUser)} will sign in with the new username. Their email (${renameUser.email ?? "—"}) and password stay the same.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            <Label htmlFor="rename-username">New username</Label>
            <div className="relative">
              <AtSign className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="rename-username"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                className="pl-9"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <UsernameCheckHint state={renameCheck} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeRenameDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleRename}
              disabled={
                updateMutation.isPending ||
                renameCheck.status === "taken" ||
                renameCheck.status === "invalid"
              }
            >
              {updateMutation.isPending
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>
                : "Save username"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}